import React from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Form, Input, Select, DatePicker, Checkbox, Upload, message } from 'antd';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import ImgCrop from 'antd-img-crop';
import Heading from '../../../components/heading/heading';
import { RelatedDocUpload } from '../Pages/GlobalComponents/RelatedDocUpload'
import { getBaseURL } from '../../../Api/ApiConfig';
import { mobileOnChangeValidator, nameOnChangeValidator, validateZipCode, validateUrlField, validateEmailField, validateDescriptionField } from '../../../components/utilities/utilities';
import PipelineStagesArrow from '../Pages/DetailsPage/PipelineStages'

const { Option } = Select;

const getSpan = (fieldType) => {
    if (fieldType === "LongString") {
        return { xs: 24, sm: 24, md: 24 };
    }
    if (fieldType === "Yes") {
        return { xs: 24, sm: 12, md: 12 };
    }
    return { xs: 24, sm: 12, md: 8 };
};

export function validateNumberField(value) {
    if (value === undefined || value === null || value === '') return null;
    const allowedRegex = /^[0-9]*$/;
    if (!allowedRegex.test(value)) return 'Only numbers are allowed';
    return null;
}

export function validateCurrencyField(value, decimalPlace = 2) {
    if (value === undefined || value === null || value === '') return null;
    const basicNumberCheck = /^\d+(\.\d*)?$/;
    if (!basicNumberCheck.test(value)) return 'Please enter a valid number';
    const decimalRegex = new RegExp(`^\\d+(\\.\\d{1,${decimalPlace}})?$`);
    if (!decimalRegex.test(value)) return `Only up to ${decimalPlace} decimal place${decimalPlace > 1 ? 's' : ''} are allowed`;
    return null;
}

export function validatePercentageField(value, decimalPlace = 2) {
    if (value === undefined || value === null || value === '') return null;
    const basicNumberCheck = /^\d+(\.\d*)?$/;
    if (!basicNumberCheck.test(value)) return 'Please enter a valid number';
    const decimalRegex = new RegExp(`^\\d+(\\.\\d{1,${decimalPlace}})?$`);
    if (!decimalRegex.test(value)) return `Only up to ${decimalPlace} decimal place${decimalPlace > 1 ? 's' : ''} are allowed`;
    const num = Number(value);
    if (num < 0 || num > 100) return 'Percentage must be between 0 and 100';
    return null;
}

export const resolveFieldConfig = (configEntry, context) => {
    if (!configEntry) return null;
    if (typeof configEntry === "function") return configEntry(context);
    return configEntry;
};

const FIELDTYPE_VALIDATOR_MAP = {
    String: (value) => nameOnChangeValidator(value, 20),
    Number: (value) => validateNumberField(value),
    Currency: (value, currencySettings) => validateCurrencyField(value, currencySettings?.decimalOfCurrency ?? 2),
    Percentage: (value, currencySettings) => validatePercentageField(value, currencySettings?.decimalOfCurrency ?? 2),
    LongString: (value) => validateDescriptionField(value, 2500)
};

const FIELD_VALIDATOR_MAP = {
    postcode: (value) => validateZipCode(value, 25),
    email: (value) => validateEmailField(value, 50),
    website: (value) => validateUrlField(value, 2000),
    fax: (value) => mobileOnChangeValidator(value, 20),
    phone: (value) => mobileOnChangeValidator(value, 20),
    street: (value) => nameOnChangeValidator(value, 100),
    linkedinUrl: (value) => validateUrlField(value, 2000),
    mobile: (value) => mobileOnChangeValidator(value, 20),
    primaryEmail: (value) => validateEmailField(value, 50),
    accountName: (value) => nameOnChangeValidator(value, 50),
    designation: (value) => nameOnChangeValidator(value, 50),
    secondaryEmail: (value) => validateEmailField(value, 50),
    caseCreatorEmail: (value) => validateEmailField(value, 50),
    caseCreatorNumber: (value) => mobileOnChangeValidator(value, 20),
    assitantContactNumber: (value) => mobileOnChangeValidator(value, 20),
    caseNumber: (value, _, sharedProps) => { return sharedProps?.validateCode?.(value) },
    productCode: (value, _, sharedProps) => { return sharedProps?.ProductCode?.(value) },
    accountNumber: (value, _, sharedProps) => { return sharedProps?.validateCode?.(value) },
};

const FIELD_SPEVALIDATOR_MAP = {
    startAfterClosureAndBeforeEnd: (value, _, sharedProps, allAttributes, fieldLabel) => {
        const closure = sharedProps?.form.getFieldValue('expectedClosureDate');
        const end = sharedProps?.form.getFieldValue('revenueEndDate');
        const closureLabel = allAttributes?.find(item => item.fieldKey === 'expectedClosureDate')?.fieldLabel;
        const endLabel = allAttributes?.find(item => item.fieldKey === 'revenueEndDate')?.fieldLabel;

        if (!value) return Promise.resolve();

        if (closure && value.isBefore(closure, 'day')) {
            return `${fieldLabel} cannot be before ${closureLabel}`
        }
        if (end && value.isAfter(end, 'day')) {
            return `${fieldLabel} must be before ${endLabel}`;
        }
        return null;
    },
}
const buildRules = ({ mandatory, fieldLabel, fieldType, fieldKey }, sharedProps, currencySettings, dealCheck, allAttributes) => {
    const rules = [];
    const conditionalFields = ['website', 'industry'];
    const isDisabled = conditionalFields.includes(fieldKey) && sharedProps?.dataDisables?.isDisable;

    const isRequired = dealCheck?.required ?? (mandatory === 'Yes' && !isDisabled);
    const Dependendfield = dealCheck?.dependsOn?.length
        ? allAttributes.find((item) => dealCheck.dependsOn.includes(item.fieldKey))
        : null;

    if (isRequired) {
        const rule = {
            required: true,
            message: `Please ${['Dropdown', 'Multi-Dropdown'].includes(fieldType)
                ? 'select'
                : 'enter'
                } ${dealCheck?.customelabel ? dealCheck.customelabel : fieldLabel}`,
        };

        if (['String', 'LongString'].includes(fieldType)) {
            rule.whitespace = true;
        }
        rules.push(sharedProps?.edit ? rule : { required: false });
    }
    rules.push({
        validator: (_, value) => {
            if (
                sharedProps?.pageTypes === "Deal" &&
                fieldKey === "accountName"
            ) {
                return Promise.resolve();
            }

            if (FIELD_VALIDATOR_MAP[fieldKey]) {
                const fieldError = FIELD_VALIDATOR_MAP[fieldKey](value, currencySettings, sharedProps);
                if (fieldError) {
                    return Promise.reject(new Error(fieldError));
                }
            }
            else if (dealCheck?.prefixItem === "%") {
                const typeError = FIELDTYPE_VALIDATOR_MAP.Percentage(value, currencySettings);
                if (typeError) {
                    return Promise.reject(new Error(typeError));
                }
            }
            else if (FIELDTYPE_VALIDATOR_MAP[fieldType]) {
                const typeError = FIELDTYPE_VALIDATOR_MAP[fieldType](value, currencySettings);
                if (typeError) {
                    return Promise.reject(new Error(typeError));
                }
            }
            if (Dependendfield && dealCheck?.dependsOn?.length === 1 && dealCheck?.validationType && sharedProps?.form) {
                const otherValue = sharedProps?.form.getFieldValue(dealCheck.dependsOn);
                if (value && otherValue) {
                    if (dealCheck.validationType === "dateAfter" && value < otherValue) {
                        return Promise.reject(
                            new Error(`${fieldLabel} must be after ${Dependendfield.fieldLabel}`)
                        );
                    }
                    if (dealCheck.validationType === "dateBefore" && value > otherValue) {
                        return Promise.reject(
                            new Error(`${fieldLabel} must be before ${Dependendfield.fieldLabel}`)
                        );
                    }
                }
            }
            if (dealCheck?.dependsOn?.length > 1 && FIELD_SPEVALIDATOR_MAP[dealCheck?.validationType]) {
                const fieldError = FIELD_SPEVALIDATOR_MAP[dealCheck?.validationType](value, currencySettings, sharedProps, allAttributes, fieldLabel);
                if (fieldError) {
                    return Promise.reject(new Error(fieldError));
                }
            }
            return Promise.resolve();
        }
    });
    return rules;
};

function AutoField({ attribute, options, disabled, specialFields, sharedProps, profile, currencySettings, allAttributes, fromDeal }) {
    const { fieldKey, fieldLabel, fieldType } = attribute;
    const conditionalFields = ['website', 'industry', 'rating', 'noofemployees', 'annualrevenue', "caseCreatorEmail", "accountName"];
    const ph = `${['Dropdown', 'Multi-Dropdown'].includes(fieldType) ? 'Select' : 'Enter'} ${fieldLabel}`;
    const isDisabled = conditionalFields.includes(fieldKey) && sharedProps?.dataDisables?.isDisable;
    const resolved = sharedProps?.dealFieldConfig?.[fieldKey] || null
    const DealSpical = resolveFieldConfig(resolved, sharedProps);
    const renderControl = () => {
        switch (fieldType) {
            case 'String':
                return (
                    <Input
                        placeholder={ph} onChange={(e) => DealSpical?.callback?.(e.target.value)}
                        disabled={DealSpical?.disabled || disabled || isDisabled}
                    />
                );
            case 'LongString':
                return (
                    <Input.TextArea
                        rows={3} onChange={(e) => DealSpical?.callback?.(e.target.value)}
                        showCount maxLength={2500}
                        placeholder={ph}
                        disabled={DealSpical?.disabled || disabled || isDisabled}
                    />
                );
            case 'Number':
                return (
                    <Input onChange={(e) => DealSpical?.callback?.(e.target.value)}
                        style={{ width: '100%' }}
                        placeholder={ph}
                        disabled={DealSpical?.disabled || disabled || isDisabled}
                    />
                );
            case 'Currency':
                return (
                    <Input  {...(DealSpical?.prefixItem === '%'
                        ? { min: 0, max: 100 }
                        : {})}
                        style={{ width: '100%' }}
                        placeholder={DealSpical?.prefixItem ? `Enter ${DealSpical.customelabel}` : ph}
                        disabled={DealSpical?.disabled || disabled || isDisabled}
                        prefix={DealSpical?.prefixItem ? DealSpical?.prefixItem : (currencySettings?.symbol || '$')}
                        onChange={(e) => DealSpical?.callback?.(e.target.value)}
                        onBlur={(e) => {
                            if (e.target.value === '' || e.target.value === undefined || e.target.value === null) {
                                if (DealSpical?.reset === "Yes") {
                                    sharedProps?.form.setFieldsValue({ [fieldKey]: 0 });
                                    DealSpical?.callback?.(0);
                                }
                            }
                        }}
                    />
                );
            case 'Percentage':
                return (
                    <Input prefix="%" min={0} max={100}
                        style={{ width: '100%' }}
                        placeholder={ph}
                        disabled={DealSpical?.disabled || disabled || isDisabled}
                        onChange={(e) => DealSpical?.callback?.(e.target.value)}
                        onBlur={(e) => {
                            if (e.target.value === '' || e.target.value === undefined || e.target.value === null) {
                                if (DealSpical?.reset === "Yes") {
                                    sharedProps?.form.setFieldsValue({ [fieldKey]: 0 });
                                    DealSpical?.callback?.(0);
                                }
                            }
                        }}
                    />
                );
            case 'Dropdown':
                return (
                    <Select
                        style={{ width: '100%' }}
                        placeholder={ph}
                        showSearch
                        allowClear={attribute.mandatory !== 'Yes'}
                        disabled={DealSpical?.disabled || disabled || isDisabled}
                        optionFilterProp="children"
                        filterOption={(input, opt) =>
                            opt.children.toLowerCase().includes(input.toLowerCase())
                        }
                        onChange={(value, option) => {
                            if (fieldKey === 'accountType') {
                                sharedProps?.onAccountTypeChange?.(value, option);
                            }
                            else if (fieldKey === 'dealType') {
                                sharedProps?.handleDealTypeChange?.(value, option);
                            }
                            else if (fieldKey === 'subtype') {
                                sharedProps?.handleTypeAction?.(value, option);
                            }
                            else {
                                DealSpical?.callback?.(option.children)
                            }
                        }}
                    >
                        {(options || []).map((opt, i) => (
                            <Option key={opt.value ?? i} value={opt.value}>{opt.label}</Option>
                        ))}
                    </Select>
                );
            case 'Multi-Dropdown':
                return (
                    <Select
                        mode="multiple"
                        className="MultiUiNew"
                        style={{ width: '100%' }}
                        placeholder={ph}
                        showSearch
                        allowClear={attribute.mandatory !== 'Yes'}
                        disabled={DealSpical?.disabled || disabled || isDisabled}
                        optionFilterProp="children"
                        filterOption={(input, opt) =>
                            opt.children.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {(options || []).map((opt, i) => (
                            <Option key={opt.value ?? i} value={opt.value}>{opt.label}</Option>
                        ))}
                    </Select>
                );
            case 'Date':
                return (
                    <DatePicker
                        allowClear={attribute.mandatory !== 'Yes'}
                        style={{ width: '100%' }}
                        placeholder={ph}
                        disabled={DealSpical?.enableOverEdit ?
                            DealSpical?.disabled
                            : (DealSpical?.disabled || disabled || isDisabled)}
                        format={profile.dateFormate ? profile.dateFormate : "YYYY-MM-DD"}
                        disabledDate={DealSpical?.disabledDate || sharedProps?.disabledDate}
                    />
                );
            case 'CheckBox':
                return <Checkbox disabled={DealSpical?.disabled || disabled || isDisabled}
                    onChange={(e) => DealSpical?.callback?.(e.target.checked)}
                />;
            default:
                return <Input placeholder={ph} disabled={DealSpical?.disabled || disabled || isDisabled} />;
        }
    };
    const control = specialFields?.[fieldKey]
        ? specialFields[fieldKey](sharedProps)
        : renderControl();
    if (!control) return null;
    return (
        <Col
            {...getSpan(fromDeal === "Yes" ? fromDeal : fieldKey === "pipelineStage" ? "LongString" : fieldType)}
            style={{ marginBottom: 5 }}
        >
            <Form.Item
                label={DealSpical?.customelabel ? DealSpical.customelabel :
                    (fieldKey === "pipelineStage" ? "" : (attribute?.fieldType === "Currency" ? `${fieldLabel} (${currencySettings?.currency || '(USD)'})` :
                        attribute?.fieldType === "Percentage" ? `${fieldLabel} (%)` : fieldLabel))}
                name={fieldKey}
                id={fieldKey}
                rules={fieldKey === "pipelineStage" ? [{ required: false }] :
                    buildRules(attribute, sharedProps, currencySettings, DealSpical, allAttributes)}
                valuePropName={fieldType === "CheckBox" ? "checked" : "value"}
            >
                {fieldKey === "pipelineStage" ?
                    <PipelineStagesArrow
                        stages={options}
                        currentStage={sharedProps?.pipelineData}
                        onStageClick={(index) => sharedProps?.handlePipeLineChange(sharedProps?.pipeLineStage, index, sharedProps?.tempWinProp)}
                        completedColor="#007bff"
                        upcomingColor="#e0e0e0"
                        textColor="#fff"
                        arrowHeight={40}
                        notchSize={6}
                        editableType={sharedProps?.editableType}
                        edit={sharedProps?.edit}
                        displayData={sharedProps?.displayData}
                        oldPipeline={sharedProps?.oldPipeline}
                        pipeLineStage={options}
                    />
                    : control}
            </Form.Item>
        </Col>
    );
}

AutoField.propTypes = {
    attribute: PropTypes.object.isRequired,
    options: PropTypes.array,
    disabled: PropTypes.bool,
    specialFields: PropTypes.objectOf(PropTypes.func),
    sharedProps: PropTypes.object,
    profile: PropTypes.object,
    currencySettings: PropTypes.object,
    allAttributes: PropTypes.array,
    fromDeal: PropTypes.string,
};

AutoField.defaultProps = {
    options: [],
    disabled: false,
};

function FileUploadGroup({ attribute, edit, combinedUrl, moduleIcon, moduleLetter, sharedProps }) {
    const { fieldKey, fieldLabel } = attribute;

    const image = sharedProps?.images?.[fieldKey] ?? null;
    const deleteOption = sharedProps?.deleteOptions?.[fieldKey] ?? false;
    const setImage = sharedProps?.setImage;        // (fieldKey, url) => void
    const setDeleteOpt = sharedProps?.setDeleteOption; // (fieldKey, bool) => void

    const uploadConfig = {
        name: 'file',
        multiple: false,
        maxCount: 1,
        accept: 'image/*',
        action: `${getBaseURL()}/usermgmt/uploadFile`,
        onChange({ file }) {
            if (file.status === 'done') {
                setImage?.(fieldKey, file.response?.mediaUrl);
                setDeleteOpt?.(fieldKey, true);
                message.success(`${file.name} uploaded successfully`);
            } else if (file.status === 'error') {
                message.error(`${file.name} upload failed`);
            }
        },
        listType: 'picture',
        showUploadList: false,
    };

    return (
        <Col span={8} style={{ marginBottom: 20 }}>
            <figure className="photoUpload align-center-v">
                {image
                    ? <img src={image} alt={fieldLabel} />
                    : <span className={`details_span_Tag ${moduleIcon || ''}`}>{moduleLetter || '?'}</span>
                }
                {edit && (
                    <figcaption>
                        {deleteOption ? (
                            <div className="delete-div">
                                <span className="delete-span">
                                    <FeatherIcon
                                        icon="trash-2"
                                        className="icon-svg"
                                        size={16}
                                        onClick={() => {
                                            setImage?.(fieldKey, null);
                                            setDeleteOpt?.(fieldKey, false);
                                        }}
                                    />
                                </span>
                            </div>
                        ) : (
                            <ImgCrop aspect={1} cropShape="round" rotationSlider modalTitle="Crop Image">
                                <Upload {...uploadConfig}>
                                    <Link className="btn-upload" to={combinedUrl || '#'}>
                                        <FeatherIcon icon="camera" size={16} />
                                    </Link>
                                </Upload>
                            </ImgCrop>
                        )}
                    </figcaption>
                )}
            </figure>
        </Col>
    );
}
FileUploadGroup.propTypes = {
    attribute: PropTypes.object, edit: PropTypes.bool, combinedUrl: PropTypes.string, moduleIcon: PropTypes.string,
    moduleLetter: PropTypes.string, sharedProps: PropTypes.object,
};
function MultiFileUploadGroup({ attribute, edit, sharedProps }) {
    const { fieldKey } = attribute;

    const fileList = sharedProps?.fileLists?.[fieldKey] || [];
    const setFileLst = sharedProps?.setFileList; // (fieldKey, list) => void

    const uploadConfig = {
        name: 'file',
        multiple: true,
        action: `${getBaseURL()}/usermgmt/uploadFile`,
        fileList,
        onChange({ fileList: newList }) {
            setFileLst?.(fieldKey, newList);
        },
        listType: 'text',
    };

    return (
        <Col span={24} style={{ marginBottom: 20 }}>
            <Upload {...uploadConfig} disabled={!edit}>
                {edit && (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '4px 12px', border: '1px dashed #d9d9d9',
                        borderRadius: 4, cursor: 'pointer', color: '#666',
                    }}>
                        <FeatherIcon icon="upload" size={14} /> Upload Files
                    </div>
                )}
            </Upload>
        </Col>
    );
}
MultiFileUploadGroup.propTypes = {
    attribute: PropTypes.object, edit: PropTypes.bool, sharedProps: PropTypes.object
};

function renderAttribute(attr, specialFields, sharedProps, optionsMap, edit, profile, currencySettings, allAttributes, fromDeal) {
    return (
        <AutoField specialFields={specialFields} sharedProps={sharedProps}
            key={attr.fieldId ?? attr.fieldKey}
            attribute={attr}
            options={optionsMap?.[attr.fieldKey] || optionsMap?.[attr.dropdownId] || []}
            disabled={!edit}
            profile={profile} currencySettings={currencySettings} allAttributes={allAttributes} fromDeal={fromDeal}
        />
    );
}

function CommonFormFields({
    groups,
    customFields,
    specialFields,
    sharedProps,
    optionsMap,
    combinedUrl,
    moduleIcon,
    moduleLetter,
    fromDeal,
}) {
    const localData = useSelector((state) => state.localRedux);
    const ProfileData = localData.profile
    const profile = ProfileData
    const currencySettings = localData.currency
    if (!groups?.length) return null;
    const sortedGroups = [...groups].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const allAttributes = Object.values(customFields).flat();
    return (
        <>
            {sortedGroups.map((group) => {
                const attributes = [...(customFields[group.customFieldGroupId] || [])]
                    .filter(attr => attr.fieldVisible === 'Yes')
                    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
                if (!attributes.length) return null;

                const fileUploadAttr = attributes.find(a => a.fieldType === 'FileUpload');
                const multiFileAttr = attributes.find(a => a.fieldType === 'MultiFileUpload');

                if (fileUploadAttr) {
                    return (
                        <React.Fragment key={group.customFieldGroupId}>
                            <Heading as="h5">{group.label}</Heading>
                            <Row gutter={[16, 16]} style={{ padding: "15px 15px 25px" }}>
                                <FileUploadGroup
                                    attribute={fileUploadAttr}
                                    edit={sharedProps?.edit}
                                    combinedUrl={combinedUrl}
                                    moduleIcon={moduleIcon}
                                    moduleLetter={moduleLetter}
                                    sharedProps={sharedProps}
                                />
                            </Row>
                        </React.Fragment>
                    );
                }

                if (multiFileAttr) {
                    return (
                        <React.Fragment key={group.customFieldGroupId}>
                            <RelatedDocUpload
                                edit={sharedProps?.edit}
                                FILEList={sharedProps?.fileLists?.[multiFileAttr.fieldKey] || []}
                                setFileList={(list) => sharedProps?.setFileList?.(multiFileAttr.fieldKey, list)}
                                form={sharedProps?.form}
                            />
                        </React.Fragment>
                    );
                }
                return (
                    <React.Fragment key={group.customFieldGroupId}>
                        <Heading as="h5">{group.label}</Heading>
                        <Row gutter={[16, 16]} style={{ padding: "15px 15px 25px" }}>
                            {attributes.map(attr =>
                                renderAttribute(attr, specialFields, sharedProps, optionsMap, sharedProps?.edit, profile, currencySettings || {}, allAttributes, fromDeal)
                            )}
                        </Row>
                    </React.Fragment>
                );
            })}
        </>
    );
}

CommonFormFields.propTypes = {
    groups: PropTypes.arrayOf(PropTypes.shape({
        customFieldGroupId: PropTypes.number,
        label: PropTypes.string,
        value: PropTypes.string,
        sortOrder: PropTypes.number,
        fieldGroupType: PropTypes.string,
    })).isRequired,
    customFields: PropTypes.objectOf(PropTypes.array).isRequired,
    specialFields: PropTypes.objectOf(PropTypes.func),
    sharedProps: PropTypes.object,
    optionsMap: PropTypes.object,
    edit: PropTypes.bool,
    combinedUrl: PropTypes.string,
    moduleIcon: PropTypes.string,
    moduleLetter: PropTypes.string,
    fromDeal: PropTypes.string,
};

CommonFormFields.defaultProps = {
    specialFields: {},
    sharedProps: {},
    optionsMap: {},
    edit: false,
    combinedUrl: '#',
    moduleIcon: '',
    moduleLetter: '?',
};

export default CommonFormFields;